import {
  Box,
  Button,
  Divider,
  HStack,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { StepProps } from "./types";
import Container from "./components/Container";
import InfoField from "./components/InfoField";
import CreativeReview from "./components/CreativeReview";
import { useMemo } from "react";
import { FiEdit, FiPlus, FiTrash } from "react-icons/fi";
import {
  MarketingAd,
  MarketingAdCreative,
  PublisherPlatforms,
} from "common/interfaces/KYDMarketing";
import dayjs from "dayjs";
import { dineroFormatCurrency } from "src/utils";

// Custom function to format date range in the desired format
const formatDateRange = (
  startDate: dayjs.Dayjs,
  endDate: dayjs.Dayjs
): string => {
  const formatStart = startDate.format("MMM D h:mmA");
  const formatEnd = endDate.format("MMM Do h:mmA");
  return `${formatStart}–${formatEnd}`;
};

const formatPlacements = (ads: MarketingAd[] = []) => {
  const rules = ads.map((ad) => ad.creative?.customization_rules).flat();
  return rules
    ?.map((rule) => {
      if (!rule || !rule.customization) {
        return "";
      }

      if ("facebook_positions" in rule?.customization) {
        return rule.customization.facebook_positions.join(", ");
      }
      if ("instagram_positions" in rule?.customization) {
        return rule.customization.instagram_positions.join(", ");
      }
      return "";
    })
    .join(", ");
};

const ReviewForm = ({
  campaign,
  onDeleteAdset,
  onDeleteCreative,
  pages,
  navigateTo,
  setDefaultEditingAd,
  setDefaultEditingAdset,
  instagramAccounts,
  onDuplicateAdset,
}: StepProps) => {
  const adsets = useMemo(() => {
    return campaign?.adsets || [];
  }, [campaign]);

  return (
    <VStack
      w="full"
      alignItems={"flex-start"}
      gap={4}
      rounded="xl"
      bg="#FAFAFA"
      p={[4, 8]}
    >
      <Text fontSize="lg" fontWeight="bold">
        Campaign Review
      </Text>
      <Container>
        <VStack w="full" alignItems={"flex-start"} gap={2}>
          <HStack w="full" justifyContent={"space-between"}>
            <VStack w="full" alignItems={"flex-start"} spacing={0}>
              <Text fontSize="sm" color="gray.500">
                Campaign
              </Text>
              <Text fontWeight="bold">{campaign?.name || "Campaign"}</Text>
            </VStack>
            <Button
              variant="outline"
              colorScheme="gray"
              onClick={() => {
                navigateTo("edit_campaign");
              }}
            >
              Edit
            </Button>
          </HStack>
          <HStack gap={8} alignItems={"flex-start"}>
            <InfoField title="Objective" value={campaign?.campaign_type} />
            <InfoField
              title="Budget Strategy"
              value={campaign?.budget?.type || "Ad Set Budget"}
            />
            {campaign?.budget && (
              <InfoField
                title="Budget"
                value={
                  campaign?.budget?.amount
                    ? dineroFormatCurrency(campaign?.budget?.amount?.amount)
                    : "Campaign Budget"
                }
              />
            )}
          </HStack>
        </VStack>
      </Container>
      {adsets.map((adset, adsetIndex) => (
        <Container
          key={adset.id || `adset-${adsetIndex}`}
          as={VStack}
          w="full"
          alignItems={"flex-start"}
          gap={8}
          p={[4, 6]}
        >
          <VStack w="full" alignItems={"flex-start"} gap={8}>
            <VStack gap={4} w="full">
              <HStack w="full" justifyContent={"space-between"}>
                <VStack w="full" alignItems={"flex-start"} spacing={0}>
                  <Text fontSize="sm" color="gray.500">
                    Ad Set
                  </Text>
                  <Text fontWeight="bold">{adset?.name || "Ad Set"}</Text>
                </VStack>
                <HStack>
                  <Button
                    variant="outline"
                    colorScheme="gray"
                    onClick={() => {
                      setDefaultEditingAdset(adset);
                      navigateTo("edit_adset");
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    colorScheme="gray"
                    onClick={() => {
                      onDuplicateAdset(adset);
                    }}
                  >
                    Duplicate
                  </Button>
                  <Button
                    variant="outline"
                    colorScheme="red"
                    rightIcon={<FiTrash />}
                    onClick={() => {
                      onDeleteAdset(adset);
                    }}
                  >
                    Delete
                  </Button>
                </HStack>
              </HStack>
              <HStack
                gap={[3, 10]}
                flexShrink={0}
                alignItems={"flex-start"}
                flexWrap="wrap"
                w="full"
              >
                <InfoField
                  title="Schedule"
                  value={formatDateRange(
                    dayjs(adset?.schedule?.start_time),
                    dayjs(adset?.schedule?.stop_time)
                  )}
                />
                {adset.budget ? (
                  <InfoField
                    title="Budget Strategy"
                    value={adset?.budget?.type}
                  />
                ) : null}
                <InfoField
                  title="Budget"
                  value={
                    adset?.budget?.amount
                      ? dineroFormatCurrency(adset?.budget?.amount?.amount)
                      : "Campaign Budget"
                  }
                />
              </HStack>
              <HStack
                gap={4}
                flexShrink={0}
                alignItems={"flex-start"}
                w="full"
                flexWrap="wrap"
              >
                {adset?.targeting?.custom_audiences?.length > 0 && (
                  <InfoField
                    title="Audience"
                    value={adset?.targeting?.custom_audiences
                      ?.map((c) => c.name)
                      .join(", ")}
                  />
                )}
                <InfoField
                  title="Age Range"
                  value={`${adset?.targeting?.demographics?.age_min} - ${adset?.targeting?.demographics?.age_max}`}
                />
                {adset?.targeting?.demographics?.genders?.length > 0 ? (
                  <InfoField
                    title="Genders"
                    value={adset?.targeting?.demographics?.genders
                      ?.map((gender) => gender)
                      .join(", ")}
                  />
                ) : null}
                {adset?.targeting?.locations?.geo_markets?.length > 0 && (
                  <InfoField
                    title="Location"
                    value={adset?.targeting?.locations?.geo_markets
                      ?.map((market) => market.value)
                      .join(", ")}
                  />
                )}
                <InfoField
                  title="Placements"
                  value={formatPlacements(adset?.ads)}
                />
              </HStack>
            </VStack>
          </VStack>
          <Container p={4}>
            <VStack w="full" alignItems={"flex-start"} gap={2}>
              <HStack w="full" justifyContent={"space-between"}>
                <Text whiteSpace="wrap" fontWeight="bold">
                  Ad creative
                </Text>
                <Button
                  variant="outline"
                  colorScheme="gray"
                  onClick={() => {
                    setDefaultEditingAdset(adset);
                    navigateTo("create_creative");
                  }}
                  rightIcon={<FiPlus />}
                >
                  Add Creative
                </Button>
              </HStack>
              <Divider borderColor="gray.200" w="full" my={4} />
              <VStack gap={4}>
                {adset.ads.map((ad, adIndex) => (
                  <VStack
                    w="full"
                    alignItems={"flex-start"}
                    gap={8}
                    key={ad.id || `ad-${adsetIndex}-${adIndex}`}
                  >
                    <HStack w="full" justifyContent={"space-between"}>
                      <Text fontWeight="bold">{ad.name}</Text>
                      <Stack direction={["column", "row"]}>
                        <Button
                          variant="outline"
                          colorScheme="gray"
                          rightIcon={<FiEdit />}
                          onClick={() => {
                            setDefaultEditingAdset(adset);
                            setDefaultEditingAd(ad);
                            navigateTo("edit_creative");
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          colorScheme="red"
                          rightIcon={<FiTrash />}
                          onClick={() => {
                            onDeleteCreative(adset, ad);
                          }}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </HStack>
                    <CreativeReview
                      images={ad.creative?.assets?.images}
                      videos={ad.creative?.assets?.videos}
                      primaryText={ad.creative?.assets?.bodies?.[0]?.text}
                      callToAction={
                        ad.creative?.assets?.call_to_actions?.[0]?.type
                      }
                      headlineText={
                        ad.creative?.assets?.descriptions?.[0]?.text
                      }
                      facebookPageId={
                        adset.identity?.[PublisherPlatforms.facebook]?.page_id
                      }
                      instagramActorId={
                        adset.identity?.[PublisherPlatforms.instagram]
                          ?.instagram_actor_id
                      }
                      eventLink={ad.creative?.assets?.links?.[0]?.url}
                      pages={pages}
                      instagramAccounts={instagramAccounts}
                    />
                  </VStack>
                ))}
              </VStack>
            </VStack>
          </Container>
        </Container>
      ))}
      {adsets.length === 0 && (
        <VStack w="full" alignItems={"flex-start"} gap={2}>
          <Text fontSize="sm" color="gray.500">
            No ad sets found
          </Text>
          <Button
            variant="outline"
            colorScheme="gray"
            onClick={() => {
              navigateTo("create_adset");
            }}
          >
            Create a new ad set
          </Button>
        </VStack>
      )}
    </VStack>
  );
};

export default ReviewForm;
